import { useState } from 'react';
import { FileText, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const QuoteButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_type: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'YOUR_WEB3FORMS_ACCESS_KEY', // Replace with your Web3Forms key
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service_type: formData.service_type,
          message: formData.message,
          subject: `New Quote Request - ${formData.service_type} from ${formData.name} - LMR IMPEX`,
          from_name: 'LMR IMPEX Website',
          to_email: 'admin@lmrimpex.com'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Thank you! Your quote request has been submitted successfully.');
        setFormData({ name: '', email: '', phone: '', company: '', service_type: '', message: '' });
        setIsOpen(false);
      } else {
        alert('Failed to submit quote request. Please try again.');
      }
    } catch (error) {
      alert('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        data-testid="quote-button-float"
        className="fixed bottom-24 right-8 z-50 bg-slate-900 text-white px-6 py-3 shadow-lg hover:bg-slate-800 transition-all duration-300 flex items-center gap-2 text-xs font-bold uppercase tracking-widest"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.2, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.05 }}
      >
        <FileText className="w-4 h-4" />
        Request Quote
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
            data-testid="quote-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-lg p-8 shadow-2xl relative"
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 transition-colors"
                data-testid="quote-modal-close-button"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>

              <h2 className="text-3xl font-bold mb-2">Request a Quote</h2>
              <p className="text-muted-foreground mb-6">Fill out the form and we'll get back to you within 24 hours.</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="quote-name-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="quote-email-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="quote-phone-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <input
                  type="text"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  data-testid="quote-company-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                />
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  data-testid="quote-service-select"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                >
                  <option value="">Select Service *</option>
                  <option value="Modular Furniture">Modular Furniture</option>
                  <option value="Interior Design">Interior Design</option>
                  <option value="Coconut Exports">Coconut Exports</option>
                  <option value="Turnkey Projects">Turnkey Projects</option>
                  <option value="Other">Other</option>
                </select>
                <textarea
                  placeholder="Your Message *"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="quote-message-input"
                  rows="4"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-button"
                  className="w-full bg-slate-900 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuoteButton;
